import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { LayoutDashboard, ScrollText } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import {
  Shield, Plus, Pencil, Trash2, Upload, Tag, Users, BookOpen, Library,
  Ticket, Loader2, CheckCircle2, X, Headphones, Link2, LifeBuoy, Mail,
} from "lucide-react";
import { toast } from "sonner";
import UserManager from "../components/admin/UserManager";
import SupportManager from "../components/admin/SupportManager";
import EmailTemplateManager from "../components/admin/EmailTemplateManager";

const COLLECTIONS = [
  { key: "states", label: "States", fields: [["name", "text"], ["code", "text"]] },
  { key: "license-types", label: "License Types", fields: [["name", "text"], ["code", "text"], ["state_id", "ref:states"]] },
  { key: "courses", label: "Courses", fields: [["name", "text"], ["description", "textarea"], ["license_type_id", "ref:license-types"], ["order", "number"]] },
  { key: "topics", label: "Topics", fields: [["name", "text"], ["description", "textarea"], ["course_id", "ref:courses"], ["order", "number"]] },
];

/* ---------------- Generic content CRUD ---------------- */
function ContentManager() {
  const [col, setCol] = useState("states");
  const [items, setItems] = useState([]);
  const [refs, setRefs] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const schema = COLLECTIONS.find((c) => c.key === col);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/content/${col}`);
    setItems(data);
    const needed = [...new Set(schema.fields.filter((f) => f[1].startsWith("ref:")).map((f) => f[1].split(":")[1]))];
    const map = {};
    for (const r of needed) { const res = await api.get(`/admin/content/${r}`); map[r] = res.data; }
    setRefs(map);
  }, [col, schema]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ active: true }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/admin/content/${col}/${editing.id}`, form);
      else await api.post(`/admin/content/${col}`, form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => { if (!window.confirm("Delete this item?")) return; await api.delete(`/admin/content/${col}/${id}`); toast.success("Deleted"); load(); };

  const label = (it) => it.name || it.title || it.code || it.id;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <Select value={col} onValueChange={setCol}>
          <SelectTrigger className="w-48" data-testid="admin-collection-select"><SelectValue /></SelectTrigger>
          <SelectContent>{COLLECTIONS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={openNew} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="admin-add-item-btn"><Plus className="h-4 w-4 mr-1" /> Add {schema.label.replace(/s$/, "")}</Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white divide-y" data-testid="admin-content-list">
        {items.length === 0 && <div className="p-6 text-slate-400 text-sm">No items yet.</div>}
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between p-4">
            <div>
              <div className="font-medium text-[#0A192F]">{label(it)}</div>
              {it.description && <div className="text-xs text-slate-500 line-clamp-1">{it.description}</div>}
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => openEdit(it)}><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => del(it.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="admin-content-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} {schema.label.replace(/s$/, "")}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            {schema.fields.map(([name, type]) => (
              <div key={name}>
                <Label className="capitalize">{name.replace(/_/g, " ").replace(" id", "")}</Label>
                {type === "textarea" ? (
                  <Textarea value={form[name] || ""} onChange={(e) => setForm({ ...form, [name]: e.target.value })} className="mt-1.5" />
                ) : type.startsWith("ref:") ? (
                  <Select value={form[name] || ""} onValueChange={(v) => setForm({ ...form, [name]: v })}>
                    <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{(refs[type.split(":")[1]] || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.name || r.code}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input type={type} value={form[name] ?? ""} onChange={(e) => setForm({ ...form, [name]: type === "number" ? Number(e.target.value) : e.target.value })} className="mt-1.5" />
                )}
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={save} data-testid="admin-save-item-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Lessons (with audio + workbook) ---------------- */
function LessonManager() {
  const [lessons, setLessons] = useState([]);
  const [topics, setTopics] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const [l, t] = await Promise.all([api.get("/admin/content/lessons"), api.get("/admin/content/topics")]);
    setLessons(l.data); setTopics(t.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ active: true, is_sample: false, workbook_content: "", audio_duration: 0 }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form };
      if (editing) await api.put(`/admin/content/lessons/${editing.id}`, payload);
      else await api.post("/admin/content/lessons", payload);
      toast.success("Lesson saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => { if (!window.confirm("Delete this lesson?")) return; await api.delete(`/admin/content/lessons/${id}`); load(); };

  const uploadAudio = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("kind", "audio");
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // try to read duration client-side
      const audio = document.createElement("audio");
      audio.src = URL.createObjectURL(file);
      audio.onloadedmetadata = () => setForm((f) => ({ ...f, audio_file_id: data.file_id, audio_filename: data.filename, audio_duration: Math.round(audio.duration) }));
      setForm((f) => ({ ...f, audio_file_id: data.file_id, audio_filename: data.filename }));
      toast.success(`Uploaded ${data.filename}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const topicName = (id) => topics.find((t) => t.id === id)?.name || "—";

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-heading font-semibold text-[#0A192F]">Lessons, Audio &amp; Workbook</h3>
        <Button onClick={openNew} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="admin-add-lesson-btn"><Plus className="h-4 w-4 mr-1" /> Add Lesson</Button>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white divide-y" data-testid="admin-lesson-list">
        {lessons.map((it) => (
          <div key={it.id} className="flex items-center justify-between p-4">
            <div className="min-w-0">
              <div className="font-medium text-[#0A192F] truncate flex items-center gap-2">{it.title} {it.is_sample && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Sample</Badge>}</div>
              <div className="text-xs text-slate-500">{topicName(it.topic_id)} · {it.audio_file_id ? "🔊 Audio set" : "No audio"}</div>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button size="sm" variant="ghost" onClick={() => openEdit(it)}><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => del(it.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="admin-lesson-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Lesson</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div><Label>Title</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Topic</Label>
              <Select value={form.topic_id || ""} onValueChange={(v) => { const t = topics.find((x) => x.id === v); setForm({ ...form, topic_id: v, course_id: t?.course_id }); }}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select topic" /></SelectTrigger>
                <SelectContent>{topics.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Workbook Content (Markdown)</Label><Textarea rows={6} value={form.workbook_content || ""} onChange={(e) => setForm({ ...form, workbook_content: e.target.value })} className="mt-1.5 font-mono text-xs" /></div>
            <div>
              <Label>Audio File</Label>
              <div className="mt-1.5 flex items-center gap-3">
                <label className="flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 py-2 cursor-pointer hover:border-amber-400 text-sm" data-testid="admin-audio-upload">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload MP3
                  <input type="file" accept="audio/*" className="hidden" onChange={(e) => uploadAudio(e.target.files[0])} />
                </label>
                {form.audio_file_id && <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> {form.audio_filename || "audio set"} ({form.audio_duration || 0}s)</span>}
              </div>
            </div>
            <div className="flex items-center justify-between"><Label>Free Sample</Label><Switch checked={!!form.is_sample} onCheckedChange={(v) => setForm({ ...form, is_sample: v })} data-testid="admin-lesson-sample" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={save} data-testid="admin-save-lesson-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Questions ---------------- */
function QuestionManager() {
  const [items, setItems] = useState([]);
  const [topics, setTopics] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    const [q, t] = await Promise.all([api.get("/admin/content/questions"), api.get("/admin/content/topics")]);
    setItems(q.data); setTopics(t.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ active: true, is_sample: false, choices: ["", "", "", ""], correct_index: 0, explanation: "" }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it, choices: [...it.choices] }); setOpen(true); };

  const save = async () => {
    try {
      const t = topics.find((x) => x.id === form.topic_id);
      const payload = { ...form, course_id: t?.course_id };
      if (editing) await api.put(`/admin/content/questions/${editing.id}`, payload);
      else await api.post("/admin/content/questions", payload);
      toast.success("Question saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete question?")) return; await api.delete(`/admin/content/questions/${id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-heading font-semibold text-[#0A192F]">Quiz Question Bank ({items.length})</h3>
        <Button onClick={openNew} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="admin-add-question-btn"><Plus className="h-4 w-4 mr-1" /> Add Question</Button>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white divide-y max-h-[500px] overflow-y-auto" data-testid="admin-question-list">
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between p-4">
            <div className="min-w-0 pr-4"><div className="text-sm text-[#0A192F] line-clamp-1">{it.question}</div><div className="text-xs text-slate-400">Correct: {String.fromCharCode(65 + it.correct_index)}</div></div>
            <div className="flex gap-1 shrink-0"><Button size="sm" variant="ghost" onClick={() => openEdit(it)}><Pencil className="h-4 w-4" /></Button><Button size="sm" variant="ghost" className="text-red-500" onClick={() => del(it.id)}><Trash2 className="h-4 w-4" /></Button></div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="admin-question-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Question</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div><Label>Topic</Label>
              <Select value={form.topic_id || ""} onValueChange={(v) => setForm({ ...form, topic_id: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select topic" /></SelectTrigger>
                <SelectContent>{topics.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Question</Label><Textarea rows={2} value={form.question || ""} onChange={(e) => setForm({ ...form, question: e.target.value })} className="mt-1.5" /></div>
            <div>
              <Label>Choices (select the correct one)</Label>
              <div className="space-y-2 mt-1.5">
                {(form.choices || []).map((c, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <button type="button" onClick={() => setForm({ ...form, correct_index: i })} className={`h-7 w-7 rounded-full border-2 flex items-center justify-center text-xs shrink-0 ${form.correct_index === i ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-300"}`} data-testid={`admin-correct-${i}`}>{String.fromCharCode(65 + i)}</button>
                    <Input value={c} onChange={(e) => { const ch = [...form.choices]; ch[i] = e.target.value; setForm({ ...form, choices: ch }); }} />
                  </div>
                ))}
              </div>
            </div>
            <div><Label>Explanation</Label><Textarea rows={3} value={form.explanation || ""} onChange={(e) => setForm({ ...form, explanation: e.target.value })} className="mt-1.5" /></div>
            <div className="flex items-center justify-between"><Label>Free Sample</Label><Switch checked={!!form.is_sample} onCheckedChange={(v) => setForm({ ...form, is_sample: v })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={save} data-testid="admin-save-question-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Coupons ---------------- */
function AdminDashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <div className="text-slate-400 text-sm p-4">Loading…</div>;
  const cards = [
    ["Total Users", s.total_users], ["Paid Users", s.paid_users], ["Free Users", s.free_users],
    ["Active Full-Access", s.active_full_access], ["New This Month", s.new_users_this_month],
    ["Conversion Rate", `${s.conversion_rate}%`], ["Expiring (14d)", s.expiring_access],
  ];
  return (
    <div data-testid="admin-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map(([label, val]) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs text-slate-500">{label}</div>
            <div className="font-heading text-2xl font-bold text-[#0A192F] mt-1">{val}</div>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-5">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h4 className="font-heading font-semibold text-[#0A192F] text-sm mb-2">Recent Registrations</h4>
          <div className="divide-y text-sm">
            {s.recent_registrations.map((u) => (
              <div key={u.id} className="py-2 flex justify-between"><span className="text-[#0A192F]">{u.name || u.email}</span><span className="text-xs text-slate-400">{u.access_type}</span></div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h4 className="font-heading font-semibold text-[#0A192F] text-sm mb-2">Recent Purchases</h4>
          <div className="divide-y text-sm">
            {s.recent_purchases.length === 0 ? <p className="text-slate-400 py-2">None yet.</p> : s.recent_purchases.map((p) => (
              <div key={p.id} className="py-2 flex justify-between"><span className="text-[#0A192F]">${(p.amount || 0).toFixed(2)}{p.coupon_code ? ` · ${p.coupon_code}` : ""}</span><span className="text-xs text-slate-400">{p.created_at ? new Date(p.created_at).toLocaleDateString() : ""}</span></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function AuditLog() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="admin-audit">
      <h3 className="font-heading font-semibold text-[#0A192F] mb-3 flex items-center gap-2"><ScrollText className="h-5 w-5 text-amber-600" /> Admin Activity</h3>
      <div className="rounded-xl border border-slate-200 bg-white divide-y">
        {rows.length === 0 ? <div className="p-6 text-slate-400 text-sm">No admin actions logged yet.</div> : rows.map((r) => (
          <div key={r.id} className="p-3 text-sm flex justify-between">
            <div><span className="font-medium text-[#0A192F]">{r.action}</span><span className="text-slate-500"> · {r.target}</span><div className="text-xs text-slate-400">{r.admin_email}{(r.before != null || r.after != null) ? ` · ${r.before ?? "—"} → ${r.after ?? "—"}` : ""}</div></div>
            <span className="text-xs text-slate-400">{r.at ? new Date(r.at).toLocaleString() : ""}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CouponManager() {
  const [items, setItems] = useState([]);
  const [freeUsage, setFreeUsage] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [redOpen, setRedOpen] = useState(false);
  const [redemptions, setRedemptions] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/coupons");
    setItems(data);
    try { const fu = await api.get("/admin/free-code-usage"); setFreeUsage(fu.data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    try { const { data } = await api.get("/admin/generate-code"); setForm((f) => ({ ...f, code: data.code })); toast.success("Code generated"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openNew = () => { setForm({ discount_type: "percentage", discount_value: 20, per_customer_limit: 1, max_redemptions: "", expires_at: "", active: true, label: "", code: "" }); setOpen(true); };

  const create = async () => {
    try {
      await api.post("/admin/coupons", form);
      toast.success("Coupon created"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggle = async (c) => { await api.put(`/admin/coupons/${c.id}`, { active: !c.active }); load(); };
  const remove = async (c) => { if (!window.confirm("Deactivate this coupon?")) return; await api.delete(`/admin/coupons/${c.id}`); load(); };
  const viewRedemptions = async (c) => { const { data } = await api.get(`/admin/coupons/${c.id}/redemptions`); setRedemptions(data); setRedOpen(true); };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading font-semibold text-[#0A192F] flex items-center gap-2"><Ticket className="h-5 w-5 text-amber-600" /> Private Agency Codes</h3>
        <Button onClick={openNew} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="admin-add-coupon-btn"><Plus className="h-4 w-4 mr-1" /> Create Code</Button>
      </div>
      <p className="text-xs text-slate-500 mb-5">These codes are private and never shown publicly. Share them directly with agency owners.</p>

      <div className="rounded-xl border border-slate-200 bg-white divide-y" data-testid="admin-coupon-list">
        {items.length === 0 && <div className="p-6 text-slate-400 text-sm">No codes yet.</div>}
        {items.map((c) => (
          <div key={c.id} className="p-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono font-bold text-[#0A192F]">{c.code}</span>
                {c.active ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge> : <Badge variant="secondary">Inactive</Badge>}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {c.discount_type === "percentage" ? `${c.discount_value}% off` : `$${c.discount_value} off`}
                {c.label && ` · ${c.label}`}
                {` · ${c.uses ?? c.redemptions ?? 0} used`}
                {c.max_redemptions ? ` / ${c.max_redemptions}` : ""}
                {c.expires_at ? ` · exp ${new Date(c.expires_at).toLocaleDateString()}` : ""}
              </div>
              {c.first_claimed_email && <div className="text-xs text-slate-400 mt-0.5" data-testid={`admin-coupon-firstclaim-${c.code}`}>First claimed by {c.first_claimed_email}{c.first_claimed_at ? ` · ${new Date(c.first_claimed_at).toLocaleDateString()}` : ""}</div>}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button size="sm" variant="ghost" onClick={() => viewRedemptions(c)} data-testid={`admin-coupon-redemptions-${c.code}`}>History</Button>
              <Switch checked={c.active} onCheckedChange={() => toggle(c)} data-testid={`admin-coupon-toggle-${c.code}`} />
              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => remove(c)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      {freeUsage.length > 0 && (
        <div className="mt-6">
          <h4 className="font-heading font-semibold text-[#0A192F] text-sm mb-2">Free-access codes redeemed</h4>
          <div className="rounded-xl border border-slate-200 bg-white divide-y" data-testid="admin-free-code-list">
            {freeUsage.map((f) => (
              <div key={f.code} className="p-3 flex items-center justify-between text-sm">
                <div><span className="font-mono font-bold text-[#0A192F]">{f.code}</span>{f.first_claimed_email && <span className="text-xs text-slate-400 ml-2">first: {f.first_claimed_email}</span>}</div>
                <span className="text-xs text-slate-500">{f.uses} used</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="admin-coupon-dialog">
          <DialogHeader><DialogTitle>Create Discount Code</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div><Label>Code</Label>
              <div className="flex gap-2 mt-1.5">
                <Input maxLength={7} value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="font-mono uppercase" placeholder="AGENT20" data-testid="admin-coupon-code-input" />
                <Button type="button" variant="outline" onClick={generate} data-testid="admin-generate-code-btn">Generate</Button>
              </div>
              <p className="text-xs text-slate-400 mt-1">Must be exactly 7 characters.</p>
            </div>
            <div><Label>Label (agency name — internal only)</Label><Input value={form.label || ""} onChange={(e) => setForm({ ...form, label: e.target.value })} className="mt-1.5" placeholder="Ivy Consulting Group" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Discount Type</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="admin-coupon-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percentage">Percentage %</SelectItem><SelectItem value="fixed">Fixed $</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Value</Label><Input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: Number(e.target.value) })} className="mt-1.5" data-testid="admin-coupon-value" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Uses per customer</Label><Input type="number" min={1} value={form.per_customer_limit} onChange={(e) => setForm({ ...form, per_customer_limit: Number(e.target.value) })} className="mt-1.5" /></div>
              <div><Label>Total redemption limit</Label><Input type="number" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} className="mt-1.5" placeholder="Unlimited" /></div>
            </div>
            <div><Label>Expiration date (optional)</Label><Input type="date" value={form.expires_at ? form.expires_at.slice(0, 10) : ""} onChange={(e) => setForm({ ...form, expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={create} data-testid="admin-save-coupon-btn">Create Code</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={redOpen} onOpenChange={setRedOpen}>
        <DialogContent data-testid="admin-redemptions-dialog">
          <DialogHeader><DialogTitle>Redemption History</DialogTitle></DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto">
            {redemptions.length === 0 ? <p className="text-sm text-slate-400 py-4">No redemptions yet.</p> : (
              <div className="divide-y">{redemptions.map((r) => (
                <div key={r.id} className="py-3 flex justify-between text-sm">
                  <div><div className="font-medium text-[#0A192F]">{r.email || r.user_email || r.user_id}</div><div className="text-xs text-slate-400">{new Date(r.at || r.created_at).toLocaleString()}</div></div>
                  <div className="text-right"><div className="text-slate-600 capitalize">{r.kind || "discount"}</div>{typeof r.amount === "number" && <div className="text-xs text-slate-400">${(r.amount || 0).toFixed(2)}</div>}</div>
                </div>
              ))}</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Author & Books ---------------- */
function AuthorManager() {
  const [form, setForm] = useState({ name: "", bio: "", books: [] });

  useEffect(() => { api.get("/author").then((r) => { const d = r.data; setForm({ name: d.name || "", bio: d.bio || "", books: d.books || [] }); }); }, []);

  const save = async () => {
    try { await api.put("/admin/author", form); toast.success("Author section updated"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addBook = () => setForm({ ...form, books: [...form.books, { title: "", url: "" }] });
  const setBook = (i, k, v) => { const b = [...form.books]; b[i][k] = v; setForm({ ...form, books: b }); };
  const rmBook = (i) => setForm({ ...form, books: form.books.filter((_, j) => j !== i) });

  return (
    <div className="max-w-xl">
      <h3 className="font-heading font-semibold text-[#0A192F] mb-1 flex items-center gap-2"><BookOpen className="h-5 w-5 text-amber-600" /> About the Author &amp; Books</h3>
      <p className="text-xs text-slate-500 mb-5">Enter your real details. Leave blank to show the placeholder on the landing page. Nothing is auto-generated.</p>
      <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
        <div><Label>Author Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="admin-author-name" /></div>
        <div><Label>Bio</Label><Textarea rows={5} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1.5" data-testid="admin-author-bio" /></div>
        <div>
          <div className="flex items-center justify-between"><Label>Published Books (Amazon links)</Label><Button size="sm" variant="outline" onClick={addBook} data-testid="admin-add-book-btn"><Plus className="h-4 w-4 mr-1" /> Add Book</Button></div>
          <div className="space-y-3 mt-2">
            {form.books.map((b, i) => (
              <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-2">
                <div className="flex justify-between items-center"><span className="text-xs text-slate-400">Book {i + 1}</span><button onClick={() => rmBook(i)} className="text-red-400"><X className="h-4 w-4" /></button></div>
                <Input value={b.title} onChange={(e) => setBook(i, "title", e.target.value)} placeholder="Book title" data-testid={`admin-book-title-${i}`} />
                <Input value={b.url} onChange={(e) => setBook(i, "url", e.target.value)} placeholder="https://www.amazon.com/…" data-testid={`admin-book-url-${i}`} />
              </div>
            ))}
          </div>
        </div>
        <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={save} data-testid="admin-save-author-btn">Save Author Section</Button>
      </div>
    </div>
  );
}

/* ---------------- Chapter Audio ---------------- */
function fmtDur(s) {
  if (!s) return "";
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

function AudioManager() {
  const [chapters, setChapters] = useState([]);
  const [busy, setBusy] = useState(null);      // chapter id currently working
  const [progress, setProgress] = useState({}); // {id: percent}
  const [urls, setUrls] = useState({});          // {id: url string}
  const [newTitle, setNewTitle] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => { const { data } = await api.get("/admin/audio-chapters"); setChapters(data.sort((a, b) => (a.order || 0) - (b.order || 0))); }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (ch, file) => {
    if (!file) return;
    setBusy(ch.id); setProgress({ ...progress, [ch.id]: 0 });
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/admin/chapters/${ch.id}/audio-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => { if (e.total) setProgress((p) => ({ ...p, [ch.id]: Math.round((e.loaded / e.total) * 100) })); },
      });
      toast.success(`Uploaded to "${ch.title}" (${fmtDur(data.duration)})`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(null); setProgress((p) => ({ ...p, [ch.id]: undefined })); }
  };

  const importUrl = async (ch) => {
    const url = (urls[ch.id] || "").trim();
    if (!url) return;
    setBusy(ch.id);
    try {
      const { data } = await api.post(`/admin/chapters/${ch.id}/audio-from-url`, { url });
      toast.success(`Imported to "${ch.title}" (${fmtDur(data.duration)})`);
      setUrls({ ...urls, [ch.id]: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed"); }
    finally { setBusy(null); }
  };

  const remove = async (ch) => { if (!window.confirm("Remove audio from this chapter?")) return; await api.delete(`/admin/chapters/${ch.id}/audio`); load(); };

  const addChapter = async () => {
    const t = newTitle.trim();
    if (!t) return;
    setAdding(true);
    try { await api.post("/admin/audio-chapters", { title: t }); setNewTitle(""); toast.success("Chapter directory added"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAdding(false); }
  };
  const renameChapter = async (ch) => {
    const t = window.prompt("Rename chapter", ch.title);
    if (t == null || !t.trim()) return;
    await api.put(`/admin/content/workbook-chapters/${ch.id}`, { title: t.trim() });
    toast.success("Renamed"); load();
  };
  const deleteChapter = async (ch) => {
    if (!window.confirm(`Delete "${ch.title}"? This removes the chapter and its audio slot.`)) return;
    await api.delete(`/admin/content/workbook-chapters/${ch.id}`);
    toast.success("Chapter deleted"); load();
  };

  return (
    <div>
      <h3 className="font-heading font-semibold text-[#0A192F] mb-1 flex items-center gap-2"><Headphones className="h-5 w-5 text-amber-600" /> Chapter Audio</h3>
      <p className="text-xs text-slate-500 mb-4">Upload a large recording directly, or paste a direct download link and the server will fetch it — no chat size limit. Each file is stored privately and streamed only to authorized users.</p>
      <div className="flex gap-2 mb-5">
        <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="New chapter title (e.g., Chapter 21: …)" data-testid="admin-add-chapter-input" />
        <Button onClick={addChapter} disabled={adding} className="bg-amber-600 hover:bg-amber-700 text-white shrink-0" data-testid="admin-add-chapter-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </div>
      <div className="space-y-4" data-testid="admin-audio-list">
        {chapters.map((ch) => {
          const working = busy === ch.id;
          const pct = progress[ch.id];
          return (
            <div key={ch.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-audio-chapter-${ch.order}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-[#0A192F] truncate">{ch.title}</div>
                  {ch.audio_file_id ? (
                    <div className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> {ch.audio_filename || "audio set"}{ch.audio_duration ? ` · ${fmtDur(ch.audio_duration)}` : ""}</div>
                  ) : (
                    <div className="text-xs text-slate-400">No audio yet</div>
                  )}
                </div>
                {!working && (
                  <div className="flex items-center gap-1 shrink-0">
                    {ch.audio_file_id && <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => remove(ch)} data-testid={`admin-audio-remove-${ch.order}`} title="Remove audio"><X className="h-4 w-4" /></Button>}
                    <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => renameChapter(ch)} data-testid={`admin-rename-chapter-${ch.order}`} title="Rename chapter"><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteChapter(ch)} data-testid={`admin-delete-chapter-${ch.order}`} title="Delete chapter"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                )}
              </div>

              {working && pct !== undefined ? (
                <div className="mt-3">
                  <div className="h-2 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-amber-500 transition-all" style={{ width: `${pct}%` }} /></div>
                  <div className="text-xs text-slate-500 mt-1 font-mono">Uploading… {pct}%</div>
                </div>
              ) : working ? (
                <div className="mt-3 text-xs text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Working…</div>
              ) : (
                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                  <label className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 py-2.5 cursor-pointer hover:border-amber-400 text-sm text-slate-600" data-testid={`admin-audio-upload-${ch.order}`}>
                    <Upload className="h-4 w-4" /> Upload file
                    <input type="file" accept="audio/*,.mp3,.m4a,.m4b,.wav" className="hidden" onChange={(e) => upload(ch, e.target.files[0])} />
                  </label>
                  <div className="flex gap-2">
                    <Input value={urls[ch.id] || ""} onChange={(e) => setUrls({ ...urls, [ch.id]: e.target.value })} placeholder="Paste direct audio link" className="text-sm" data-testid={`admin-audio-url-${ch.order}`} />
                    <Button variant="outline" size="sm" className="shrink-0" onClick={() => importUrl(ch)} data-testid={`admin-audio-import-${ch.order}`}><Link2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Admin shell ---------------- */
export default function Admin() {
  return (
    <Layout showFooter={false}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-10 w-10 rounded-lg bg-[#0A192F] flex items-center justify-center"><Shield className="h-5 w-5 text-amber-500" /></div>
          <div><h1 className="font-heading text-2xl font-bold text-[#0A192F]">Admin Console</h1><p className="text-sm text-slate-500">Manage content, questions, codes, and users — no code required.</p></div>
        </div>

        <Tabs defaultValue="dashboard">
          <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100 p-1">
            <TabsTrigger value="dashboard" data-testid="admin-tab-dashboard"><LayoutDashboard className="h-4 w-4 mr-1" /> Dashboard</TabsTrigger>
            <TabsTrigger value="library" data-testid="admin-tab-library"><Library className="h-4 w-4 mr-1" /> Structure</TabsTrigger>
            <TabsTrigger value="lessons" data-testid="admin-tab-lessons"><BookOpen className="h-4 w-4 mr-1" /> Lessons</TabsTrigger>
            <TabsTrigger value="audio" data-testid="admin-tab-audio"><Headphones className="h-4 w-4 mr-1" /> Audio</TabsTrigger>
            <TabsTrigger value="questions" data-testid="admin-tab-questions"><Pencil className="h-4 w-4 mr-1" /> Questions</TabsTrigger>
            <TabsTrigger value="coupons" data-testid="admin-tab-coupons"><Tag className="h-4 w-4 mr-1" /> Codes</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="h-4 w-4 mr-1" /> Users</TabsTrigger>
            <TabsTrigger value="support" data-testid="admin-tab-support"><LifeBuoy className="h-4 w-4 mr-1" /> Support</TabsTrigger>
            <TabsTrigger value="emails" data-testid="admin-tab-emails"><Mail className="h-4 w-4 mr-1" /> Emails</TabsTrigger>
            <TabsTrigger value="author" data-testid="admin-tab-author"><BookOpen className="h-4 w-4 mr-1" /> Author</TabsTrigger>
            <TabsTrigger value="activity" data-testid="admin-tab-activity"><ScrollText className="h-4 w-4 mr-1" /> Activity</TabsTrigger>
          </TabsList>
          <div className="mt-6">
            <TabsContent value="dashboard"><AdminDashboard /></TabsContent>
            <TabsContent value="library"><ContentManager /></TabsContent>
            <TabsContent value="lessons"><LessonManager /></TabsContent>
            <TabsContent value="audio"><AudioManager /></TabsContent>
            <TabsContent value="questions"><QuestionManager /></TabsContent>
            <TabsContent value="coupons"><CouponManager /></TabsContent>
            <TabsContent value="users"><UserManager /></TabsContent>
            <TabsContent value="support"><SupportManager /></TabsContent>
            <TabsContent value="emails"><EmailTemplateManager /></TabsContent>
            <TabsContent value="author"><AuthorManager /></TabsContent>
            <TabsContent value="activity"><AuditLog /></TabsContent>
          </div>
        </Tabs>
      </div>
    </Layout>
  );
}
